import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChangeLog, Pagination, Response } from '@ui/src';
import {
  NotificationItem,
  NotificationItemComment,
  NotificationItemIssue,
  NotificationItemIssueStatus,
} from '@notifications/hoc/center/types';
import { transactionApi } from '../../transactions/store/services';

export interface TransactionsNotificationCenterState {
  notifications?: NotificationItem[];
  selectedNotification?: NotificationItem;
  selectedNotifications?: string[];
  selectedNotificationId?: string;
  pagination?: Pagination;
  comments?: NotificationItemComment[];
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  notificationEntity?: any;
  employees?: {
    id: string;
    profile: {
      firstName: string;
      lastName: string;
    };
  }[];
}

const initialState: TransactionsNotificationCenterState = {
  selectedNotifications: [],
};

export const transactionsNotificationCenterSlice = createSlice({
  name: 'transactionsNotificationCenter',
  initialState,
  reducers: {
    setSelectedNotifications: (state, action: PayloadAction<string[]>) => {
      state.selectedNotifications = action.payload;
    },
    setNotificationsItems: (
      state,
      action: PayloadAction<NotificationItem[]>,
    ) => {
      state.notifications = action.payload;
    },
    setSelectedNotification: (
      state,
      action: PayloadAction<NotificationItem | undefined>,
    ) => {
      state.selectedNotification = action.payload;
    },
    setSelectedNotificationId: (state, action: PayloadAction<string>) => {
      state.selectedNotificationId = action.payload;
    },
    clearChangeLogs: (state) => {
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
    },
    clearNotificationComments: (state) => {
      state.comments = [];
      state.notificationEntity = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsNotificationsList.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<NotificationItem>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.notifications = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextNotifications = ([] as NotificationItem[]).concat(
            state.notifications as NotificationItem[],
          );

          action.payload.data.edges?.forEach((item) => {
            if (
              !nextNotifications?.find(
                (notification) => notification.id === item.node.id,
              )
            ) {
              nextNotifications.push(item.node);
            }
          });

          state.notifications = nextNotifications;
        }

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsNotificationChangeLogs
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsNotificationItem.matchFulfilled,
      (state, action: PayloadAction<{ data: NotificationItem }>) => {
        const nextNotifications = ([] as NotificationItem[]).concat(
          state.notifications as NotificationItem[],
        );

        nextNotifications.unshift(action.payload.data);

        state.notifications = nextNotifications;
      },
    );

    builder.addMatcher(
      transactionApi.endpoints.getTransactionsNotificationEmployees
        .matchFulfilled,
      (
        state,
        action: PayloadAction<
          Response<{
            id: string;
            profile: { firstName: string; lastName: string };
          }>
        >,
      ) => {
        state.employees = action.payload?.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.batchUpdateTransactionsNotificationIssue
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: number }> & {
          meta: {
            arg: {
              originalArgs: {
                ids?: string[];
                deadline?: string;
                executorId?: string;
                responsibleId?: string;
                status?: string;
              };
            };
          };
        },
      ) => {
        const { ids, deadline, executorId, responsibleId, status } =
          action.meta.arg.originalArgs;

        state.notifications = state.notifications?.map((item) => {
          const result = { ...item };
          const issueResult = { ...result.issue };

          if (ids?.find((id) => item.id === id)) {
            if (deadline) {
              issueResult.deadline = deadline;
            }

            if (executorId) {
              issueResult.executorId = executorId;

              const currentUser = state?.employees?.find(
                (employee) => employee.id === executorId,
              );

              if (currentUser) {
                issueResult.executor = {
                  id: executorId,
                  profile: {
                    firstName: currentUser?.profile?.firstName,
                    lastName: currentUser?.profile?.lastName,
                  },
                };
              }
            }

            if (responsibleId) {
              issueResult.responsibleId = responsibleId;

              const currentUser = state?.employees?.find(
                (employee) => employee.id === responsibleId,
              );

              if (currentUser) {
                issueResult.responsible = {
                  id: responsibleId,
                  profile: {
                    firstName: currentUser?.profile?.firstName,
                    lastName: currentUser?.profile?.lastName,
                  },
                };
              }
            }

            if (status) {
              issueResult.status = status as NotificationItemIssueStatus;
            }

            issueResult.notificationId = item.id;

            result.issue = issueResult as NotificationItemIssue;
          }

          return result;
        });

        if (ids?.find((id) => id === state.selectedNotification?.id)) {
          const nextNotification = { ...state.selectedNotification };
          const nextNotificationIssue = { ...nextNotification.issue };

          if (deadline) {
            nextNotificationIssue.deadline = deadline;
          }

          if (executorId) {
            nextNotificationIssue.executorId = executorId;

            const currentUser = state?.employees?.find(
              (employee) => employee.id === executorId,
            );

            if (currentUser) {
              nextNotificationIssue.executor = {
                id: executorId,
                profile: {
                  firstName: currentUser?.profile?.firstName,
                  lastName: currentUser?.profile?.lastName,
                },
              };
            }
          }

          if (responsibleId) {
            nextNotificationIssue.responsibleId = responsibleId;

            const currentUser = state?.employees?.find(
              (employee) => employee.id === responsibleId,
            );

            if (currentUser) {
              nextNotificationIssue.responsible = {
                id: responsibleId,
                profile: {
                  firstName: currentUser?.profile?.firstName,
                  lastName: currentUser?.profile?.lastName,
                },
              };
            }
          }

          if (status) {
            nextNotificationIssue.status =
              status as NotificationItemIssueStatus;
          }

          nextNotification.issue =
            nextNotificationIssue as NotificationItemIssue;

          state.selectedNotification = nextNotification as NotificationItem;
        }
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsNotificationItemComment
        .matchFulfilled,
      (state, action: PayloadAction<Response<NotificationItemComment>>) => {
        state.comments = action.payload.data.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionNotificationTransactionEntity
        .matchFulfilled,
      (state, action: PayloadAction<{ data: any }>) => {
        state.notificationEntity = action.payload.data;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionNotificationCashCollectionEntity
        .matchFulfilled,
      (state, action: PayloadAction<{ data: any }>) => {
        state.notificationEntity = action.payload.data;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.updateTransactionsNotificationIssue
        .matchFulfilled,
      (state, action: PayloadAction<{ data: NotificationItem }>) => {
        state.notifications = state?.notifications?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = action.payload.data;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.addTransactionsNotificationItemComment
        .matchFulfilled,
      (state, action: PayloadAction<{ data: NotificationItemComment }>) => {
        let nextComments: NotificationItemComment[] = [];

        if (state.comments) {
          nextComments = ([] as NotificationItemComment[]).concat(
            state.comments,
          );
        }

        nextComments.push(action.payload.data);

        state.comments = nextComments;
      },
    );
  },
});

export const {
  setSelectedNotifications,
  clearChangeLogs,
  clearNotificationComments,
  setSelectedNotification,
  setSelectedNotificationId,
  setNotificationsItems,
} = transactionsNotificationCenterSlice.actions;

export default transactionsNotificationCenterSlice.reducer;
